import { apiClient } from '@applyonline/shared'
import {
  BaseComputeAmountForFrequencyDto,
  ComputedAmountForFrequency,
  ComputeFundingDto,
  ComputeFundingResultDto,
  ComputeEstimatedPaymentResultDto,
  MonthlyPayment
} from '@applyonline/shared/types'
import { QueryFunctionContext, useQuery } from '@tanstack/react-query'

export interface ComputeAmountForFrequencyDto extends BaseComputeAmountForFrequencyDto {
  merchantId: string
  paymentPlanId: string | null
  hasCoapplicant: boolean
  include84Months: boolean
}

const AMOUNT_FOR_FREQUENCY = 'amountForFrequency'
const AMOUNT_FOR_PROMOTIONAL_CREDIT_APP = 'amountForPromotionalCreditApp'
const ESTIMATED_PAYMENT = 'estimatedPayment'

const keysFactory = {
  amountForFrequency: (computeParams: ComputeAmountForFrequencyDto | null) =>
    [{ scope: AMOUNT_FOR_FREQUENCY }, computeParams] as const,
  amountForPromotionalCreditApp: (computeFunding: ComputeFundingDto) =>
    [{ scope: AMOUNT_FOR_PROMOTIONAL_CREDIT_APP }, computeFunding] as const,
  estimatedPayment: (creditApplicationId: string | null) =>
    [{ scope: ESTIMATED_PAYMENT }, creditApplicationId] as const,
}

const computePaymentAmountForFrequency = async ({
  queryKey: [_, computeParams],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['amountForFrequency']>>) => {
  const response = await apiClient.post('personal/CreditApplication/computeAmountForFrequency', computeParams)
  return response.data as ComputedAmountForFrequency
}

export function useComputePaymentAmountForFrequency(
  computeParams: ComputeAmountForFrequencyDto | null,
): [ComputedAmountForFrequency | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: [...keysFactory.amountForFrequency(computeParams)],
    queryFn: computePaymentAmountForFrequency,
    enabled: computeParams !== null,
  })

  return [data ?? null, isFetching]
}

const getPromotionalCreditAppPlan = async ({
  queryKey: [_, computeParams],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['amountForPromotionalCreditApp']>>) => {
  const response = await apiClient.post<ComputeFundingResultDto>(
    `personal/CreditApplication/${encodeURIComponent(computeParams.creditApplicationId)}/computeFunding`,
    computeParams,
  )
  return response.data
}

export function useGetPromotionalCreditAppPlan(
  amountRequested: number,
  creditApplicationId: string,
  paymentPlanId: string | null,
): [ComputeFundingResultDto | null, boolean] {
  const { isFetching, data } = useQuery({
    queryKey: keysFactory.amountForPromotionalCreditApp({
      amountRequested,
      creditApplicationId,
      paymentPlanId,
    } as ComputeFundingDto),
    queryFn: getPromotionalCreditAppPlan,
    enabled: !!creditApplicationId && Boolean(paymentPlanId),
  })

  return [data ?? null, isFetching]
}

const getEstimatedPayment = async ({
  queryKey: [_, creditApplicationId],
}: QueryFunctionContext<ReturnType<(typeof keysFactory)['estimatedPayment']>>) => {
  const response = await apiClient.get<ComputeEstimatedPaymentResultDto>(
    `personal/CreditApplication/${encodeURIComponent(creditApplicationId!)}/ComputeEstimatedPayment`,
  )
  return response.data
}

export function useComputeEstimatedPayment(
  creditApplicationId: string | null,
): [ComputeEstimatedPaymentResultDto | null, boolean, boolean] {
  const { data, isFetching, isError } = useQuery({
    queryKey: keysFactory.estimatedPayment(creditApplicationId),
    queryFn: getEstimatedPayment,
    enabled: creditApplicationId !== null,
  })

  return [data ?? null, isFetching, isError]
}
export type {
  BaseComputeAmountForFrequencyDto,
  ComputedAmountForFrequency,
  ComputeFundingDto,
  ComputeFundingResultDto,
  MonthlyPayment,
  ComputeEstimatedPaymentResultDto
}
